import * as React from 'react';
import * as cx from 'classnames';

import {
  withStyles,
  WithStyles,
  Theme,
  createStyles,
  Typography,
} from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import EditIcon from '@material-ui/icons/Create';
import Button from '@material-ui/core/Button';
import { ListingPreviewCard } from 'ha/services/apiV2/types/AlgoliaListing';
import units from 'ha/theme/units';
import formatPrice from 'ha/utils/formatPrice';
import buildParams from 'ha/utils/buildParams';
import Img from 'ha/ui/next/Img';
import Link from 'ha/ui/Link';

import getFirstAvailableDay from 'ha/utils/exclusionDate/firstAvailableDay';
import Badge from 'ha/ui/next/PaperBadge';
import LazyComponent from 'ha/components/LazyComponent';
import FavoriteListing from 'ha/modules/FavoriteListing';
import FavoriteButton from 'ha/components/next/FavoriteButton';
import Track from 'ha/components/track/SmartTrack';

import { Values } from 'ha/constants/Facilities';
import { withIntl, IntlProps } from 'ha/i18n';
import { Kinds } from 'ha/constants/Room';
import { withConfig, ConfigProps } from 'ha/helpers/config';

import {
  areBillsIncluded,
  getListingPreviewTitle,
} from '../../Listing/helpers';
import ListingPreviewGallery from '../../Listing/ListingPreviewGallery';

const styles = (theme: Theme) =>
  createStyles({
    card: {
      boxShadow: 'none',
      [theme.breakpoints.down('sm')]: {
        borderRadius: 0,
      },
    },
    content: {
      padding: 0,
      [theme.breakpoints.up('md')]: {
        paddingBottom: '0 !important', // https://github.com/mui-org/material-ui/issues/9871
      },
      [theme.breakpoints.down('md')]: {
        paddingLeft: units.rem(0.5),
      },
    },
    contentMarker: {
      paddingLeft: units.rem(0.5),
    },
    wrapper: {
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
    },
    mediaWrapper: {
      position: 'relative',
      borderRadius: units.rem(0.3),
      overflow: 'hidden',
      paddingBottom: '61.224%',
      [theme.breakpoints.down('sm')]: {
        borderRadius: 0,
      },
    },
    mediaWrapperMarker: {
      borderRadius: 0,
      paddingBottom: 0,
      height: '165px',
    },
    shadow: {
      top: '70%',
      bottom: 0,
      left: 0,
      right: 0,
      position: 'absolute',
      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)',
    },
    media: {
      paddingTop: '56.25%', // 16:9
    },
    badge: {
      position: 'absolute',
      left: 0,
      top: units.rem(0.5),
    },
    favorite: {
      position: 'absolute',
      right: 0,
      top: 0,
      padding: units.rem(0.5),
    },
    priceWrapper: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      padding: units.rem(0.5),
      display: 'flex',
      alignItems: 'center',
    },
    text: {
      ...theme.typography.body1,
    },
    price: {
      ...theme.typography.title,
      fontSize: 42,
      fontWeight: 'bold',
      color: theme.palette.common.white,
      textShadow: '0 2px 3px #000',
    },
    priceDetails: {
      fontWeight: 'bold',
      color: theme.palette.common.white,
      textShadow: '0 2px 3px #000',
      display: 'flex',
      flexDirection: 'column',
      marginLeft: units.rem(0.5),
    },
    priceDetailsText: {
      '&::first-letter': {
        textTransform: 'capitalize',
      },
    },
    title: {
      marginTop: units.rem(0.5),
      fontSize: units.rem(1.1),
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    description: {
      marginTop: units.rem(0.2),
      fontSize: units.rem(1),
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    editBtn: {
      backgroundColor: theme.palette.common.white,
    },
    imageWithoutGallery: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      height: '100%',
      width: 'auto',
      zIndex: 0,
    },
    listingInfoList: {
      margin: 0,
      padding: 0,
    },
  });

interface Props {
  room: ListingPreviewCard;
  isLazy?: boolean;
  isMapMarker?: boolean;
  withGallery?: boolean;
  withFavorites?: boolean;
  noFollow?: boolean;
  onMouseEnter?: (id: number) => void;
  onMouseLeave?: (id: number) => void;
  onCardClick?: () => void;
  queryParams: {
    startDate: string | null;
    endDate: string | null;
  };
  showEditBtn?: boolean;
}

export class ListingCard extends React.Component<
  Props & IntlProps & WithStyles<typeof styles> & ConfigProps
> {
  handleMouseEnter = () => {
    if (this.props.onMouseEnter) {
      this.props.onMouseEnter(this.props.room.id);
    }
  };

  handleMouseLeave = () => {
    if (this.props.onMouseLeave) {
      this.props.onMouseLeave(this.props.room.id);
    }
  };

  render() {
    const {
      room,
      intl: { formatDate, T, urlResolver },
      classes,
      withGallery,
      withFavorites,
      isLazy,
      isMapMarker,
      showEditBtn,
      onCardClick,
      queryParams,
      noFollow,
      appConfig,
    } = this.props;

    const imageTimeoutMs =
      appConfig && appConfig.thumb && appConfig.thumb.imageTimeoutMs;
    const listingKindLabel = getListingPreviewTitle({
      T,
      kind: Number(room.kind),
      type: Number(room.type),
      bedroomCount: room.facility_bedroom_count,
    });

    const firstAvailableDay = getFirstAvailableDay(
      room.exclusions,
      room.minimalRentalPeriod,
    );

    const listingDatesLabel = firstAvailableDay
      ? T('Available from %(dates)s', {
          dates: formatDate('DD MMM YYYY', firstAvailableDay),
        })
      : false;

    const isBedroomCountPresented = Boolean(room.facility_bedroom_count);
    const isAptSizePresented =
      Boolean(room.facility_total_size) || Boolean(room.facility_bedroom_size);
    const isPrivateOrSharedRoom =
      Number(room.kind) === Kinds.PrivateRoom ||
      Number(room.kind) === Kinds.SharedRoom;

    const ListingPicture =
      withGallery && room.photos ? (
        <ListingPreviewGallery
          itemProp="image"
          photos={room.photos}
          alt={`${listingDatesLabel} (${room.street}, ${room.city})`}
          isLazy={isLazy}
          showDots={false}
        />
      ) : (
        <Img
          itemProp="image"
          src={room.previewImage}
          alt={`${listingDatesLabel} (${room.street}, ${room.city})`}
          className={classes.imageWithoutGallery}
          timeoutMs={imageTimeoutMs}
          isResponsive
        />
      );

    return (
      <Link
        to={`${room.listingPath}${buildParams(queryParams)}`}
        target="_blank"
        rel={noFollow ? 'noopener noreferrer' : undefined}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        data-test-locator="Listing Card"
        onClick={onCardClick}
        itemScope
        itemType="http://schema.org/Offer"
      >
        <Card className={classes.card}>
          <div
            className={cx(classes.mediaWrapper, {
              [classes.mediaWrapperMarker]: isMapMarker,
            })}
          >
            {isLazy ? (
              <LazyComponent bottomOffset="-50%">
                <div>{ListingPicture}</div>
              </LazyComponent>
            ) : (
              ListingPicture
            )}

            {room.isNew && (
              <div className={classes.badge}>
                <Badge kind="mint-left-no-radius">{T('New')}</Badge>
              </div>
            )}

            {showEditBtn && !withFavorites && (
              <Link
                to={urlResolver.getEditListingUrl(room.id)}
                onClick={e => {
                  e.stopPropagation();
                }}
                className={classes.favorite}
                skipLangFormat
              >
                <Button variant="contained" className={classes.editBtn}>
                  <EditIcon />
                </Button>
              </Link>
            )}

            {withFavorites && !showEditBtn && (
              <div className={classes.favorite}>
                <FavoriteListing listingId={room.id}>
                  {({ onClick, isLoading, isFavorite }) => (
                    <Track
                      name={'Favorite clicked'}
                      type="button"
                      getDynamicData={() => ({
                        listingId: room.id,
                        favoriteType: isFavorite ? 'unfavorite' : 'favorite',
                      })}
                    >
                      <FavoriteButton.Dark
                        onClick={onClick}
                        isLoading={isLoading}
                        isFavorite={isFavorite}
                        size="small"
                      />
                    </Track>
                  )}
                </FavoriteListing>
              </div>
            )}

            <div className={classes.shadow} />
            <Typography className={classes.priceWrapper}>
              <span
                data-test-locator="Listing Card Rent"
                className={classes.price}
              >
                {formatPrice(room.price, room.currency)}
              </span>
              <meta
                itemProp="price"
                content={Math.floor(room.price / 100).toString()}
              />
              <meta itemProp="priceCurrency" content={room.currency} />
              <div className={`${classes.text} ${classes.priceDetails}`}>
                <span className={classes.priceDetailsText}>
                  {T('per month')}
                </span>
                {areBillsIncluded(room) && (
                  <span
                    className={classes.priceDetailsText}
                    data-test-locator="Listing Card BillsIncluded"
                  >
                    {T('bills included')}
                  </span>
                )}
              </div>
            </Typography>
          </div>
          <CardContent
            className={cx(classes.content, {
              [classes.contentMarker]: isMapMarker,
            })}
          >
            <Typography
              component="h2"
              variant="subheading"
              data-test-locator="Listing Card Title"
              itemProp="name"
              className={`${classes.text} ${classes.title}`}
            >
              {T('%(kind)s at %(street)s', {
                kind: listingKindLabel,
                street: room.street,
              })}
            </Typography>

            <ul className={classes.listingInfoList}>
              {listingDatesLabel && (
                <li
                  data-test-locator="Listing Card Availability"
                  itemProp="availabilityStarts"
                  className={`${classes.text} ${classes.description}`}
                >
                  {listingDatesLabel}
                </li>
              )}

              <li
                data-test-locator="Listing Card Extra Info"
                className={`${classes.text} ${classes.description}`}
              >
                {room.facility_bedroom_furnished === Values.Yes
                  ? T('Furnished')
                  : T('Not furnished')}{' '}
                {isBedroomCountPresented &&
                  `• ${
                    room.facility_bedroom_count === '0'
                      ? T('Studio')
                      : room.facility_bedroom_count === '1'
                      ? `1 ${T('Bedroom')}`
                      : T('%s Bedrooms', room.facility_bedroom_count)
                  } `}
                {isAptSizePresented &&
                  `• ${
                    isPrivateOrSharedRoom
                      ? room.facility_bedroom_size
                      : room.facility_total_size
                  } m²`}
              </li>
            </ul>
          </CardContent>
        </Card>
      </Link>
    );
  }
}

export default withIntl(withConfig(withStyles(styles)(ListingCard)));
